// src/SocialLinks.tsx
import React, { useState } from 'react';
import AnimationWrapper from './AnimationWrapper';
import { COMPANY_INFO, getWhatsappLink } from './config';

// Componente auxiliar para iconos de imagen
const IconImg = ({ src, className = "w-6 h-6" }: { src: string, className?: string }) => (
    <img src={src} alt="Icon" className={`object-contain ${className}`} />
);

const SocialLinks: React.FC = () => {
    
    const [showSedes, setShowSedes] = useState(false);

    // Datos de Sedes (Puedes agregar más aquí a futuro)
    const sedes = [
        { 
            name: "Sede Principal - Caracas", 
            whatsapp: COMPANY_INFO.whatsappNumber, 
            mapaUrl: COMPANY_INFO.mapsUrl,
            direccion: "Calle Sucre, Chacao." 
        },
        // Aquí podrías agregar "Sede Valencia", "Sede Lechería", etc.
    ];

    // Estilo Base de los Botones (Tema Dark/Gold)
    const buttonStyle = "w-full p-4 shadow-lg bg-gray-900 text-white flex items-center transition-all duration-300 transform hover:scale-[1.02] hover:shadow-2xl border border-gray-800 hover:border-yellow-600 group cursor-pointer relative z-10 backdrop-blur-sm bg-opacity-90";

    const links = [
        { 
            id: 1, 
            label: "Sitio Web Oficial", 
            subtext: "Explora la experiencia Bestune",
            url: "/", 
            icon: <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"></path></svg>
        },
        { 
            id: 2, 
            label: "Catálogo de Vehículos", 
            subtext: "Modelos, Precios y Fotos",
            url: "/catalogo", 
            icon: <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"></path></svg>
        },
        { 
            id: 3, 
            label: "Atención al Cliente", 
            subtext: "Chat Directo WhatsApp",
            url: getWhatsappLink("Hola, vengo de Instagram y quisiera información sobre un Bestune."), 
            icon: <IconImg src="/WhatsappLogo.webp" />
        },
    ];

    return (
        <div className="min-h-screen bg-black flex flex-col items-center py-12 px-4 relative overflow-x-hidden font-sans">
            
            {/* Fondo con Textura y Gradiente */}
            <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.webp')] opacity-20 z-0"></div>
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[150%] h-64 bg-gradient-to-b from-yellow-900/20 to-transparent z-0 blur-3xl"></div>

            <div className="z-10 w-full max-w-md flex flex-col items-center">
                
                {/* LOGO */}
                <AnimationWrapper direction="up">
                    <div className="w-32 h-32 bg-black rounded-full p-1 shadow-2xl mb-6 flex items-center justify-center border-2 border-yellow-600 ring-4 ring-yellow-600/20 mt-4 relative overflow-hidden">
                        {/* Brillo rotativo opcional */}
                        <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/10 to-transparent w-full h-full animate-pulse"></div>
                        <img src="/LogoBestune.webp" alt="Bestune Logo" className="w-full h-auto object-contain p-2" />
                    </div>
                </AnimationWrapper>

                {/* TÍTULO */}
                <AnimationWrapper direction="up" delay={100}>
                    <div className="text-center mb-10">
                        <h1 className="text-2xl font-extrabold tracking-widest text-white uppercase">
                            BESTUNE <span className="text-yellow-500">CARS</span>
                        </h1>
                        <p className="text-gray-400 text-sm mt-2 tracking-wide font-light border-t border-gray-800 pt-2 inline-block px-4">
                            Redefiniendo el Lujo Accesible
                        </p>
                    </div>
                </AnimationWrapper>

                {/* LINKS PRINCIPALES */}
                <div className="w-full space-y-4">
                    {links.map((link, index) => (
                        <AnimationWrapper key={link.id} direction="up" delay={index * 100 + 200}>
                            <a 
                                href={link.url} 
                                target={link.url.startsWith('http') ? '_blank' : '_self'}
                                rel="noopener noreferrer"
                                className={buttonStyle}
                            >
                                <div className="mr-4 flex-shrink-0 bg-black/50 p-2 border border-gray-700">
                                    {link.icon}
                                </div>
                                <div className="flex-grow text-left">
                                    <span className="font-bold text-base block text-white group-hover:text-yellow-500 transition-colors">
                                        {link.label}
                                    </span>
                                    {link.subtext && <span className="text-xs text-gray-500 block mt-0.5">{link.subtext}</span>}
                                </div>
                                <div className="ml-2 text-gray-500 group-hover:text-yellow-500 transition-colors">
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                                </div>
                            </a>
                        </AnimationWrapper>
                    ))}

                    {/* === DESPLEGABLE DE SEDES === */}
                    <AnimationWrapper direction="up" delay={500}>
                        <div className="w-full">
                            <button 
                                onClick={() => setShowSedes(!showSedes)}
                                className={`${buttonStyle} ${showSedes ? 'border-yellow-600 ring-1 ring-yellow-600/50' : ''}`}
                            >
                                <div className="mr-4 flex-shrink-0 bg-black/50 p-2 border border-gray-700">
                                    <svg className="w-6 h-6 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"></path><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"></path></svg>
                                </div>
                                <div className="flex-grow text-left">
                                    <span className="font-bold text-base block text-white group-hover:text-yellow-500 transition-colors">
                                        Ubícanos
                                    </span>
                                    <span className="text-xs text-gray-500 block mt-0.5">
                                        Ver Sedes y Direcciones
                                    </span>
                                </div>
                                <div className="ml-2 text-gray-500 group-hover:text-yellow-500 transition-colors">
                                    <svg className={`w-5 h-5 transform transition-transform duration-300 ${showSedes ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                </div>
                            </button>

                            <div className={`overflow-hidden transition-all duration-500 ease-in-out ${showSedes ? 'max-h-[500px] opacity-100 mt-3' : 'max-h-0 opacity-0'}`}>
                                <div className="bg-gray-900/80 backdrop-blur-md border border-gray-800 p-2 space-y-2">
                                    {sedes.map((sede, idx) => (
                                        <div key={idx} className="flex flex-col p-3 hover:bg-gray-800 transition duration-150 border border-transparent hover:border-gray-700">
                                            
                                            <div className="flex justify-between items-start mb-2">
                                                <span className="text-sm font-bold text-white">
                                                    {sede.name}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400 mb-3">{sede.direccion}</p>

                                            <div className="flex space-x-2">
                                                {sede.mapaUrl && (
                                                    <a 
                                                        href={sede.mapaUrl} 
                                                        target="_blank" 
                                                        rel="noopener noreferrer"
                                                        className="flex-1 flex items-center justify-center bg-gray-800 text-gray-300 text-xs font-bold px-3 py-2 border border-gray-700 hover:text-white hover:border-yellow-600 transition-all"
                                                    >
                                                        Mapa
                                                    </a>
                                                )}

                                                <a 
                                                    href={`https://wa.me/${sede.whatsapp}?text=Hola,%20quisiera%20información%20sobre%20la%20sede%20${encodeURIComponent(sede.name)}.`}
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="flex-1 flex items-center justify-center bg-green-600/20 text-green-400 text-xs font-bold px-3 py-2 border border-green-900/50 hover:bg-green-600 hover:text-white transition-all"
                                                >
                                                    <img src="/WhatsappLogo.webp" alt="wa" className="w-3 h-3 mr-2" />
                                                    Chat
                                                </a>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </AnimationWrapper>

                </div>        
                
                {/* FOOTER SIMPLE */}
                <div className="mt-12 text-gray-600 text-xs flex flex-col items-center">
                    <div className="flex space-x-6 mb-4">
                        <a href={COMPANY_INFO.instagramUrl} target="_blank" rel="noreferrer" className="transform hover:scale-110 transition opacity-60 hover:opacity-100">
                            <img src="/IntagramLogo.webp" alt="IG" className="w-6 h-6" />
                        </a>
                        <a href={COMPANY_INFO.tiktokUrl} target="_blank" rel="noreferrer" className="transform hover:scale-110 transition opacity-60 hover:opacity-100">
                            <img src="/TikTokLogo.webp" alt="TT" className="w-6 h-6" />
                        </a>
                        <a href={COMPANY_INFO.facebookUrl} target="_blank" rel="noreferrer" className="transform hover:scale-110 transition opacity-60 hover:opacity-100">
                            <img src="/FacebookLogo.webp" alt="FB" className="w-6 h-6" />
                        </a>
                    </div>
                    <p className="font-medium opacity-50">© {new Date().getFullYear()} Bestune Cars Venezuela</p>
                </div>

            </div>
        </div>
    );
};

export default SocialLinks;